/**
 * Quiz Results Page: Accessibility (WCAG AA Compliance)
 * ARIA labels, keyboard navigation, focus management, screen reader support
 */

(function() {
  'use strict';

  /**
   * Add ARIA labels to interactive elements
   */
  function addAriaLabels() {
    // Add to cart buttons
    document.querySelectorAll('[data-results-add-to-cart], [data-mobile-add-to-cart]').forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        const text = btn.textContent.trim();
        btn.setAttribute('aria-label', text || 'Add to cart');
      }
      btn.setAttribute('role', 'button');
    });

    // FAQ accordion buttons
    document.querySelectorAll('[data-faq-toggle]').forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        const question = btn.querySelector('span')?.textContent || 'Toggle FAQ';
        btn.setAttribute('aria-label', question);
      }
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');
    });

    // Product links
    document.querySelectorAll('[data-product-link]').forEach(link => {
      if (!link.getAttribute('aria-label')) {
        const productName = link.dataset.productLink || 'Product';
        link.setAttribute('aria-label', `Learn more about ${productName}`);
      }
    });

    // Social sharing buttons
    document.querySelectorAll('[data-social-share]').forEach(btn => {
      const platform = btn.dataset.socialShare || 'social media';
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', `Share on ${platform}`);
      }
    });

    // Exit intent modal
    const exitModal = document.querySelector('.veluna-exit-intent-modal');
    if (exitModal) {
      exitModal.setAttribute('role', 'dialog');
      exitModal.setAttribute('aria-modal', 'true');
      exitModal.setAttribute('aria-labelledby', 'exit-intent-title');
    }

    // Subscription select
    document.querySelectorAll('[data-subscription-frequency]').forEach(select => {
      if (!select.getAttribute('aria-label')) {
        select.setAttribute('aria-label', 'Select subscription frequency');
      }
    }
    );
  }

  /**
   * Enhance keyboard navigation
   */
  function enhanceKeyboardNavigation() {
    // Make FAQ accordion keyboard accessible
    document.querySelectorAll('[data-faq-toggle]').forEach(btn => {
      btn.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.click();
        }
      });
    });

    // Trap focus in modals
    function trapFocus(modal) {
      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      modal.addEventListener('keydown', function(e) {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
        if (e.key === 'Escape') {
          const closeBtn = modal.querySelector('[aria-label*="Close"], .veluna-exit-intent-modal__close');
          if (closeBtn) closeBtn.click();
        }
      });
    }

    // Apply focus trap to modals
    document.querySelectorAll('.veluna-exit-intent-modal, .veluna-email-results-modal').forEach(modal => {
      trapFocus(modal);
    });
  }

  /**
   * Add skip links for screen readers
   */
  function addSkipLinks() {
    const skipLink = document.createElement('a');
    skipLink.href = '#quiz-results-main';
    skipLink.className = 'veluna-skip-link';
    skipLink.textContent = 'Skip to main content';
    skipLink.setAttribute('aria-label', 'Skip to main quiz results content');
    document.body.insertBefore(skipLink, document.body.firstChild);

    // Add main landmark
    const mainContent = document.querySelector('[data-quiz-results-container]');
    if (mainContent && !mainContent.id) {
      mainContent.id = 'quiz-results-main';
      mainContent.setAttribute('role', 'main');
    }
  }

  /**
   * Ensure color contrast compliance
   */
  function checkColorContrast() {
    // This would typically be done in CSS, but we can add data attributes
    // for dynamic content that needs contrast checking
    const textElements = document.querySelectorAll('.veluna-quiz-results-optimal__description, .veluna-quiz-results-enhancements__ingredient-description');
    textElements.forEach(el => {
      // Ensure minimum font size for readability
      const computedStyle = window.getComputedStyle(el);
      const fontSize = parseFloat(computedStyle.fontSize);
      if (fontSize < 14) {
        el.style.fontSize = '14px';
      }
    });
  }

  /**
   * Add focus indicators
   */
  function addFocusIndicators() {
    // Add focus-visible class support
    const style = document.createElement('style');
    style.textContent = `
      .veluna-quiz-results-optimal button:focus-visible,
      .veluna-quiz-results-enhancements button:focus-visible,
      .veluna-quiz-results-enhancements a:focus-visible,
      .veluna-quiz-results-advanced button:focus-visible {
        outline: 3px solid var(--veluna-ember);
        outline-offset: 2px;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Add screen reader announcements
   */
  function addScreenReaderAnnouncements() {
    const announcement = document.createElement('div');
    announcement.className = 'veluna-sr-only';
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.id = 'veluna-sr-announcements';
    document.body.appendChild(announcement);

    // Announce cart additions
    const originalShowNotification = window.VelunaQuizResultsCart?.showCartNotification;
    if (originalShowNotification) {
      window.VelunaQuizResultsCart.showCartNotification = function(message) {
        originalShowNotification(message);
        const srAnnouncement = document.getElementById('veluna-sr-announcements');
        if (srAnnouncement) {
          srAnnouncement.textContent = message;
          setTimeout(() => {
            srAnnouncement.textContent = '';
          }, 1000);
        }
      };
    }
  }

  /**
   * Initialize accessibility features
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Wait for content to load
    function waitForContent() {
      const resultsContainer = document.querySelector('[data-quiz-results-optimal]');
      if (resultsContainer && resultsContainer.innerHTML.trim()) {
        addAriaLabels();
        enhanceKeyboardNavigation();
        addSkipLinks();
        checkColorContrast();
        addFocusIndicators();
        addScreenReaderAnnouncements();
        console.log('Quiz Results Accessibility: WCAG AA features initialized');
      } else {
        setTimeout(waitForContent, 100);
      }
    }

    waitForContent();
  }

  init();

  // Export
  if (typeof window !== 'undefined') {
    window.VelunaQuizResultsAccessibility = {
      addAriaLabels,
      enhanceKeyboardNavigation,
      addSkipLinks,
      checkColorContrast
    };
  }
})();









